import { createEntityAdapter, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createSelectAllBySource,
  DEFAULT_SOURCE_REDUCER_STATE,
  fetchSuccess,
  ReducerState,
  upsertManyMutably,
} from "redux-thunk-kit";
import {
  DASHBOARD_STORAGE_KEYS,
  HeadshotStyleProps,
  StyleBackgroundProps,
  StyleClothingProps,
} from "~/constants/dashboard";
import {
  confirmJob,
  confirmPayment,
  fetchJobData,
  fetchMyListJob,
  fetchOutfits,
  fetchPackages,
  fetchPendingJob,
  fetchQuota,
  fetchSharedJob,
  fetchSubscriptions,
  fetchSurveys,
  photoVerify,
  removeSelectedPhoto,
  uploadPhotoMisc,
} from "~/redux/dashboards/thunk";
import {
  HeadshotStyleApiItem,
  HeadshotStyleResultApi,
  JobApiItem,
  JobPendingApiItem,
  OutfitApiItem,
  PackageApiItem,
  PhotoshootType,
  ResultImageEntity,
  SurveyApiItem,
  VerifyResultImage,
} from "~/types/models/dashboard";
import * as storage from "~/utils/storage";
import isEmpty from "lodash/isEmpty";
import { normalize } from "normalizr";
import { resultImagesSchema, SOURCES_RESULT_IMAGE } from "~/redux/dashboards/entity-config";
import mapValues from "lodash/mapValues";
import { RootState } from "~/redux/root-reducer";
import groupBy from "lodash/groupBy";
import pick from "lodash/pick";
import uniq from "lodash/uniq";
import sortBy from "lodash/sortBy";
import { ensureSafeChaining, getHeadshotStyleFromStyleId, getStyleIdFromBgAndClothing } from "~/utils/helpers";
import { Quota, Subscription } from "~/types/models/user";

export enum EDashboardStep {
  Welcome = "Welcome",
  Package = "Package",
  Styles = "Styles",
  Upload = "Upload",
  Download = "Download",
}

export interface StyleDataProps {
  bg: StyleBackgroundProps;
  clothing: StyleClothingProps;
  styleId: string;
}

export interface VerifyResult {
  invalid_images: VerifyResultImage[];
  valid_images: VerifyResultImage[];
  message?: string;
}

export interface VerifySingleResult {
  invalid_image: VerifyResultImage | null;
  valid_image: VerifyResultImage | null;
  message?: string;
}

export interface ConfirmKeyFeature {
  age?: number;
  ethnicity?: string;
  eye_color?: string;
  hair_color?: string;
  hair_length?: string;
  name?: string;
  sex?: string;
  relationship?: string;
  personNo?: number;
}

interface InitialState extends ReducerState {
  jobId: string;
  selectedType: PhotoshootType;
  packageData: PackageApiItem[];
  surveyData: SurveyApiItem[];
  styleData: HeadshotStyleProps[]; // styleData map and return { background: style.bg.value, clothing: style.clothing.value  } is a confirmStyle
  verifyResult: VerifyResult | null;
  dashboardProgress: {
    step: EDashboardStep;
    screenIndex: number;
  };
  outfitData: OutfitApiItem[];
  isActiveOutfitData: OutfitApiItem[];
  headshotStyleSelected: HeadshotStyleApiItem[];
  confirmSurvey: string[];
  confirmKeyFeature: ConfirmKeyFeature | null;
  confirmKeyFeature2?: ConfirmKeyFeature | null;
  jobData: JobApiItem[];
  jobItemData: JobApiItem | null;
  sharedJobData: JobApiItem | null;
  jobPendingData: JobPendingApiItem | null;
  headshotStyles: HeadshotStyleProps[];
  headshotStyleResults: HeadshotStyleResultApi[] | null;
  selectAllCheckboxValue: boolean;
  selectAllFavoriteCheckboxValue: boolean;
  showGoToFavPopup: boolean;
  dashboardActiveStep: number;
  didRedirect: boolean;
  subscriptions?: Subscription[];
  quota?: Quota;
}

const sources = mapValues(SOURCES_RESULT_IMAGE, () => DEFAULT_SOURCE_REDUCER_STATE);

const dashboardAdapter = createEntityAdapter<ResultImageEntity>();

// @ts-ignore
const initialState = dashboardAdapter.getInitialState<InitialState>({
  sources,
  jobId: "",
  selectedType: "one_person",
  packageData: [],
  outfitData: [],
  isActiveOutfitData: [],
  headshotStyleSelected: [],
  surveyData: [],
  confirmSurvey: [],
  styleData: [],
  verifyResult: null,
  dashboardProgress: {
    step: EDashboardStep.Welcome,
    screenIndex: 0,
  },
  dashboardActiveStep: 0,
  confirmKeyFeature: {
    // @ts-ignore
    age: "",
    ethnicity: "",
    eye_color: "",
    name: "",
    sex: "",
    hair_color: "",
    hair_length: "",
    personNo: 1,
  },
  confirmKeyFeature2: {
    // @ts-ignore
    age: "",
    ethnicity: "",
    eye_color: "",
    name: "",
    sex: "",
    hair_color: "",
    hair_length: "",
    relationship: "",
    personNo: 2,
  },
  jobData: [],
  jobItemData: null,
  sharedJobData: null,
  jobPendingData: null,
  headshotStyles: [],
  selectAllCheckboxValue: false,
  selectAllFavoriteCheckboxValue: false,
  showGoToFavPopup: false,
  didRedirect: false,
  subscriptions: [],
  quota: undefined,
});

const dashboards = createSlice({
  name: "dashboards",
  initialState,
  reducers: {
    setDashboardProgress(state, { payload }: PayloadAction<Partial<InitialState["dashboardProgress"]>>) {
      state.dashboardProgress = { ...state.dashboardProgress, ...payload };
      storage.save(DASHBOARD_STORAGE_KEYS.DASHBOARD_PROGRESS, { ...state.dashboardProgress, ...payload });
    },
    setJobId(state, { payload }: PayloadAction<string>) {
      state.jobId = payload;
    },
    setSelectedType(state, { payload }: PayloadAction<PhotoshootType>) {
      state.selectedType = payload;
    },
    setDidRedirect(state, { payload }: PayloadAction<boolean>) {
      state.didRedirect = payload;
    },
    setActiveStep(state, { payload }: PayloadAction<{ step: number }>) {
      state.dashboardActiveStep = payload.step;
    },
    setConfirmSurvey(state, { payload }: PayloadAction<string[]>) {
      state.confirmSurvey = payload;
      storage.save(DASHBOARD_STORAGE_KEYS.SURVEY_IDS, payload);
    },
    setStyleData(state, { payload }: PayloadAction<HeadshotStyleProps | HeadshotStyleProps[] | undefined>) {
      if (isEmpty(payload)) {
        state.styleData = [];
        return;
      }

      if (Array.isArray(payload)) {
        payload.forEach(i => addStyleId(i));
        state.styleData = payload;
      } else {
        addStyleId(payload);
        state.styleData = [...state.styleData, payload];
      }
    },
    setHeadshotStyleSelected(
      state,
      {
        payload,
      }: PayloadAction<{
        styles: HeadshotStyleApiItem | HeadshotStyleApiItem[] | undefined;
        mode?: "create" | "upsell";
        jobId?: string;
      }>
    ) {
      const { styles, mode = "create", jobId } = payload;
      const storageKey =
        mode === "upsell" && jobId
          ? `${DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION}_UPSELL_${jobId}`
          : DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION;

      if (isEmpty(styles)) {
        state.headshotStyleSelected = [];
        storage.save(storageKey, []);
        return;
      }

      if (Array.isArray(styles)) {
        state.headshotStyleSelected = styles;
      } else {
        state.headshotStyleSelected = [...state.headshotStyleSelected, styles];
      }

      storage.save(storageKey, state.headshotStyleSelected);
    },

    setConfirmKeyFeature(state, { payload }: PayloadAction<ConfirmKeyFeature | null>) {
      if (!payload) {
        state.confirmKeyFeature = {
          // @ts-ignore
          age: "",
          ethnicity: "",
          eye_color: "",
          name: "",
          sex: "",
          personNo: 1,
        };
      } else {
        state.confirmKeyFeature = payload;
        storage.save(DASHBOARD_STORAGE_KEYS.KEY_FEATURE, payload);
      }
    },
    setConfirmKeyFeature2(state, { payload }: PayloadAction<ConfirmKeyFeature | null>) {
      if (!payload) {
        state.confirmKeyFeature2 = {
          // @ts-ignore
          age: "",
          ethnicity: "",
          eye_color: "",
          name: "",
          sex: "",
          relationship: "",
          personNo: 2,
        };
      } else {
        state.confirmKeyFeature2 = payload;
        storage.save(DASHBOARD_STORAGE_KEYS.KEY_FEATURE_2, payload);
      }
    },
    removeStyleData(state, { payload }: PayloadAction<HeadshotStyleProps | undefined>) {
      if (!payload) {
        return;
      }
      const { bg, clothing } = payload;
      const styleId = getStyleIdFromBgAndClothing(bg?.value!, clothing?.value!);
      state.styleData = state.styleData.filter(s => s.styleId !== styleId);
    },
    removeHeadshotStyleSelected(
      state,
      {
        payload,
      }: PayloadAction<{
        style: HeadshotStyleApiItem | undefined;
        mode?: "create" | "upsell";
        jobId?: string;
      }>
    ) {
      const { style, mode = "create", jobId } = payload;

      if (!style) {
        return;
      }

      state.headshotStyleSelected = state.headshotStyleSelected.filter(
        s => s.headshot_style_id !== style.headshot_style_id
      );

      const storageKey =
        mode === "upsell" && jobId
          ? `${DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION}_UPSELL_${jobId}`
          : DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION;
      storage.save(storageKey, state.headshotStyleSelected);
    },
    setVerifyResult(state, { payload }: PayloadAction<VerifyResult>) {
      state.verifyResult = payload;
    },
    selectStyle(state, { payload }: PayloadAction<{ headshotStyle: HeadshotStyleProps; isSelected: boolean }>) {
      state.headshotStyles = state.headshotStyles.map(s => {
        if (s.styleId !== payload.headshotStyle.styleId) {
          return s;
        }
        return { ...s, isSelected: payload.isSelected };
      });
    },
    selectHeadshotStyle(
      state,
      { payload }: PayloadAction<{ headshotStyle: HeadshotStyleResultApi; isSelected: boolean }>
    ) {
      if (state.headshotStyleResults) {
        state.headshotStyleResults = state.headshotStyleResults?.map(s => {
          if (s.headshot_style_id !== payload.headshotStyle.headshot_style_id) {
            return s;
          }
          return { ...s, is_selected: payload.isSelected };
        });
      }
    },
    clearAllStylesFilter(state) {
      state.headshotStyles = state.headshotStyles.map(s => ({ ...s, isSelected: false }));
    },
    clearAllHeadshotStyleResultsFilter(state) {
      if (state.headshotStyleResults) {
        state.headshotStyleResults = state.headshotStyleResults?.map(s => ({ ...s, is_selected: false }));
      }
    },
    setShowGoToFavPopup(state, { payload }: PayloadAction<{ isShow: boolean }>) {
      state.showGoToFavPopup = payload.isShow;
    },
    setFavorite(state, { payload }: PayloadAction<{ imageId: string; isFavorite: boolean }>) {
      const { imageId, isFavorite } = payload;
      ensureSafeChaining(state.entities, [imageId, "is_favorite"]);
      // @ts-ignore
      state.entities[imageId].is_favorited = isFavorite;
    },
    voteImage(state, { payload }: PayloadAction<{ imageId: string; isLiked: boolean }>) {
      const { imageId, isLiked } = payload;
      ensureSafeChaining(state.entities, [imageId, "$isLiked"]);
      const item = state.entities[imageId]!;
      item.$isLiked = isLiked;
      let likedPhotos = storage.load(DASHBOARD_STORAGE_KEYS.LIKED_PHOTOS) || [];
      if (isLiked) {
        likedPhotos.push(imageId);
        item.like_count += 1;
      } else {
        likedPhotos = likedPhotos.filter((id: string) => id !== imageId);
        item.like_count -= 1;
      }
      storage.save(DASHBOARD_STORAGE_KEYS.LIKED_PHOTOS, uniq(likedPhotos));
    },
    selectImage(state, { payload }: PayloadAction<{ imageId: string; isSelected: boolean }>) {
      const { imageId, isSelected } = payload;
      ensureSafeChaining(state.entities, [imageId, "isSelected"]);
      const item = state.entities[imageId]!;
      item.isSelected = isSelected;
      if (!isSelected) {
        state.selectAllCheckboxValue = false;
        if (state.selectAllFavoriteCheckboxValue && item.is_favorited) {
          state.selectAllFavoriteCheckboxValue = false;
        }
      }
    },
    selectImagesByGroup(state, { payload }: PayloadAction<{ group: "all" | "favorite"; isSelected: boolean }>) {
      const { group, isSelected } = payload;
      if (group === "all") {
        const filteredStyles = state.headshotStyles.filter((hs: HeadshotStyleProps) => hs.isSelected) || [];
        const filteredStyleIds = filteredStyles.map(s => s.styleId);
        const items: ResultImageEntity[] =
          state.sources[SOURCES_RESULT_IMAGE.RESULT_PAGE]?.allIds?.map(id => state.entities[id]!) || [];
        if (isEmpty(filteredStyleIds)) {
          items.forEach(i => {
            i.isSelected = isSelected;
          });
        } else {
          const groupedAll: Record<string, ResultImageEntity[]> = groupBy(items, "$styleId");
          const groupedItems = pick(groupedAll, filteredStyleIds);
          Object.values(groupedItems).forEach(groupItem => {
            groupItem?.forEach(i => {
              i.isSelected = isSelected;
            });
          });
        }
      }
      if (group === "favorite") {
        state.sources[SOURCES_RESULT_IMAGE.RESULT_PAGE]?.allIds?.forEach(id => {
          const item = state.entities?.[id];
          if (item && item.is_favorited) {
            item.isSelected = isSelected;
          }
        });
      }
    },
    setSelectAllCheckboxes(
      state,
      { payload }: PayloadAction<{ group?: "all" | "favorite" | "all_groups"; isSelected: boolean }>
    ) {
      const { group, isSelected } = payload;
      if (group === "all") {
        state.selectAllCheckboxValue = isSelected;
        state.sources[SOURCES_RESULT_IMAGE.RESULT_PAGE]?.allIds?.forEach(id => {
          const item = state.entities?.[id];
          if (item) {
            item.is_favorited = isSelected;
          }
        });
      }
      if (group === "favorite") {
        state.selectAllFavoriteCheckboxValue = isSelected;
      }
      if (group === "all_groups") {
        state.selectAllCheckboxValue = isSelected;
        state.selectAllFavoriteCheckboxValue = isSelected;
      }
    },
    clearResultDetailData(state, { payload }: PayloadAction<{}>) {
      state.headshotStyleResults = [];
      state.jobItemData = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchPackages.fulfilled, (state, { payload }) => {
      state.packageData = payload;
      state.packageData = payload;
    });
    builder.addCase(fetchOutfits.fulfilled, (state, { payload }) => {
      state.outfitData = payload;
      const isActiveOutfitData = payload.filter(item => item.is_active);
      const outfitsWithActiveStyles = isActiveOutfitData.map(item => ({
        ...item,
        is_active_headshot_styles: item.headshot_styles?.filter(hs => hs.is_active === true) || [],
      }));
      state.isActiveOutfitData = outfitsWithActiveStyles.filter(item => item.is_active_headshot_styles?.length > 0);
    });

    builder.addCase(fetchSurveys.fulfilled, (state, { payload }) => {
      state.surveyData = payload;
    });
    builder.addCase(confirmPayment.fulfilled, (state, { payload }) => {
      state.dashboardProgress = { step: EDashboardStep.Styles, screenIndex: 0 };
      state.jobId = payload.id;
      storage.save(DASHBOARD_STORAGE_KEYS.JOB_ID, payload.id);
      storage.save(DASHBOARD_STORAGE_KEYS.DASHBOARD_PROGRESS, { step: EDashboardStep.Styles, screenIndex: 0 });
    });
    builder.addCase(photoVerify.fulfilled, (state, { payload }) => {
      state.verifyResult = payload;
      storage.save(DASHBOARD_STORAGE_KEYS.VERIFY_RESULT, payload);
    });
    builder.addCase(removeSelectedPhoto.fulfilled, (state, { payload }) => {
      const currentInvalidImages = state.verifyResult?.invalid_images || [];
      const currentValidImages = state.verifyResult?.valid_images || [];
      state.verifyResult = {
        invalid_images: currentInvalidImages.filter(image => image.id !== payload.imageId),
        valid_images: currentValidImages.filter(image => image.id !== payload.imageId),
      };
    });
    builder.addCase(uploadPhotoMisc.fulfilled, (state, { payload }) => {
      const currentInvalidImages = state.verifyResult?.invalid_images || [];
      const currentValidImages = state.verifyResult?.valid_images || [];
      const newInvalidImages = payload.map((result: any) => result.invalidImage);
      const newValidImages = payload.map((result: any) => result.validImage);

      state.verifyResult = {
        invalid_images: [...currentInvalidImages, ...newInvalidImages].filter(file => {
          const { localMeta, localUrl, errMsg, ...rest } = file || {};
          return !isEmpty(rest);
        }),
        valid_images: [...currentValidImages, ...newValidImages].filter(file => {
          const { localMeta, localUrl, errMsg, ...rest } = file || {};
          return !isEmpty(rest);
        }),
      };
      storage.save(DASHBOARD_STORAGE_KEYS.VERIFY_RESULT, state.verifyResult);
    });
    builder.addCase(fetchMyListJob.fulfilled, (state, { payload }) => {
      state.jobData = isEmpty(payload) ? [] : payload;
    });
    builder.addCase(confirmJob.fulfilled, (state, { payload, meta }) => {
      state.jobData.push(payload);
    });
    builder.addCase(fetchJobData.fulfilled, (state, { payload, meta }) => {
      if (payload.result_images) {
        const normalized = normalize(payload.result_images, [resultImagesSchema]);
        upsertManyMutably(dashboardAdapter, state, normalized.entities.result);
        fetchSuccess(state, { normalized, source: meta?.arg?.source });
        const styleIds = uniq(
          payload.result_images.map(image => getStyleIdFromBgAndClothing(image.style.background, image.style.clothing))
        );
        state.headshotStyles = styleIds?.sort()?.map(sid => getHeadshotStyleFromStyleId(sid, false));
        state.headshotStyleResults = payload?.headshot_style_results;
      }

      state.jobItemData = payload;
    });
    builder.addCase(fetchSharedJob.fulfilled, (state, { payload, meta }) => {
      if (payload.result_images) {
        const normalized = normalize(payload.result_images, [resultImagesSchema]);
        upsertManyMutably(dashboardAdapter, state, normalized.entities.result);
      }

      state.sharedJobData = payload;
    });
    builder.addCase(fetchPendingJob.fulfilled, (state, { payload }) => {
      if (!isEmpty(payload)) {
        state.jobPendingData = payload;
        state.jobId = payload.id;
        if (payload.upload_images?.length) {
          state.verifyResult = {
            invalid_images: [],
            valid_images: payload.upload_images,
          };
        }
        state.selectedType = payload.type || "one_person";
      } else {
        state.jobPendingData = null;
        state.jobId = "";
        state.verifyResult = {
          invalid_images: [],
          valid_images: [],
        };
      }
    });
    builder.addCase(fetchSubscriptions.fulfilled, (state, { payload }) => {
      if (!isEmpty(payload)) {
        state.subscriptions = payload;
      } else {
        state.subscriptions = [];
      }
    });
    builder.addCase(fetchQuota.fulfilled, (state, { payload }) => {
      if (!isEmpty(payload)) {
        state.quota = payload;
      } else {
        state.quota = undefined;
      }
    });
  },
});

export const {
  setDashboardProgress,
  setStyleData,
  removeStyleData,
  clearResultDetailData,
  removeHeadshotStyleSelected,
  setHeadshotStyleSelected,
  setConfirmSurvey,
  setConfirmKeyFeature,
  setConfirmKeyFeature2,
  setJobId,
  setSelectedType,
  setDidRedirect,
  setActiveStep,
  setVerifyResult,
  selectStyle,
  selectHeadshotStyle,
  clearAllStylesFilter,
  clearAllHeadshotStyleResultsFilter,
  setShowGoToFavPopup,
  setFavorite,
  voteImage,
  selectImage,
  selectImagesByGroup,
  setSelectAllCheckboxes,
} = dashboards.actions;

export const {
  selectById: selectResultImageById,
  selectIds: selectResultImagesIds,
  selectEntities: selectResultImagesEntities,
  selectAll: selectAllResultImages,
  selectTotal: selectTotalResultImages,
} = dashboardAdapter.getSelectors((state: RootState) => state.dashboard);

export default dashboards.reducer;

export const [getAllHeadshots] = createSelectAllBySource("dashboard", selectResultImagesEntities, [
  SOURCES_RESULT_IMAGE.RESULT_PAGE,
]);

export const getHeadshotsAllTab = createSelector(
  [getAllHeadshots, (state, params) => params],
  ({ data: resultImages }, { styleIds }) => {
    const groupedImages = groupBy(resultImages, "$styleId");
    if (isEmpty(styleIds)) {
      return groupedImages as Record<string, ResultImageEntity[]>;
    }
    return pick(groupedImages, styleIds) as Record<string, ResultImageEntity[]>;
  }
);

export const getHeadshotsFavoriteTab = createSelector(
  [getAllHeadshots, (state, params) => params],
  ({ data: resultImages }, { styleIds }) => {
    const groupedImages = groupBy(resultImages, "is_favorited");

    return {
      favorited: sortBy(groupedImages.true, ["$styleId", "created_at"]),
      others: sortBy(groupedImages.false, ["$styleId", "created_at"]),
    } as Record<string, ResultImageEntity[]>;
  }
);

export const getSelectingStyles = createSelector(
  [state => state.dashboard.headshotStyles],
  headshotStyles => headshotStyles.filter((hs: HeadshotStyleProps) => hs.isSelected) || []
);
export const getSelectingHeadshotStyles = createSelector(
  [state => state.dashboard.headshotStyleResults],
  headshotStyleResult => headshotStyleResult?.filter((hs: HeadshotStyleResultApi) => hs.is_selected) || []
);

export const getCountResultImages = createSelector([getAllHeadshots], allHeadshots => ({
  all: allHeadshots.allIds?.length || 0,
  favourite: allHeadshots.data?.filter((hs: ResultImageEntity) => hs.is_favorited)?.length || 0,
}));

export const getCountSelectedResultImages = createSelector([getAllHeadshots], allHeadshots => ({
  all: allHeadshots.data?.filter((i: ResultImageEntity) => i.isSelected)?.length || 0,
  favourite: 0,
}));

export const getSelectingImages = createSelector([selectAllResultImages], allImages =>
  allImages?.filter((i: ResultImageEntity) => i.isSelected)
);

const addStyleId = (style: HeadshotStyleProps) => {
  if (isEmpty(style)) {
    return;
  }
  if (!style?.styleId) {
    style.styleId = getStyleIdFromBgAndClothing(style?.bg?.value!, style?.clothing?.value!);
  }
};
